import { useState, useEffect, useRef, useCallback, Dispatch, SetStateAction } from 'react';
import { TelemetryPoint, mergeTelemetry } from '../utils/telemetryUtils';
import { ReactionType, ReactionCounts } from '../types/event';

const WS_BASE_URL =
  window.__ENV?.REACT_APP_WS_URL ||
  process.env.REACT_APP_WS_URL ||
  'ws://localhost:8000';

/** Maximum backoff delay in milliseconds. */
const MAX_RECONNECT_DELAY_MS = 30_000;
/** Base delay for exponential backoff. */
const BASE_RECONNECT_DELAY_MS = 1_000;
/** Stop reconnecting after this many failed attempts (~25 min at max backoff). */
const MAX_RECONNECT_ATTEMPTS = 50;

export interface UseWebSocketResult {
  capacityMessage: string | null;
  streamActive: boolean;
  disconnected: boolean;
}

export const useWebSocket = (
  hashId: string | undefined,
  enabled: boolean,
  setTelemetryPoints: Dispatch<SetStateAction<TelemetryPoint[]>>,
  setCurrentPosition: Dispatch<SetStateAction<[number, number] | null>>,
  setCurrentSpeed: Dispatch<SetStateAction<number>>,
  setCurrentAltitude: Dispatch<SetStateAction<number | null>>,
  setCurrentHeartRate: Dispatch<SetStateAction<number | null>>,
  addFloating: (type: ReactionType) => void,
  setReactionCounts: Dispatch<SetStateAction<ReactionCounts>>,
  setNarrationText: Dispatch<SetStateAction<string | null>>,
  setSpectatorCount: Dispatch<SetStateAction<number>>,
  onEventEnded?: () => void,
): UseWebSocketResult => {
  const [capacityMessage, setCapacityMessage] = useState<string | null>(null);
  const [streamActive, setStreamActive] = useState(false);
  const [disconnected, setDisconnected] = useState(false);

  // Stable refs so the connect function always reads current values
  // without needing them in the useEffect dependency array.
  const attemptsRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef(false);

  const connect = useCallback(() => {
    if (!hashId || !enabled || unmountedRef.current) return;

    // Clean up any existing connection before creating a new one.
    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* ignore */ }
      wsRef.current = null;
    }

    setCapacityMessage(null);
    const websocket = new WebSocket(`${WS_BASE_URL}/ws/spectate/${hashId}`);
    wsRef.current = websocket;

    websocket.onopen = () => {
      console.log('WebSocket connected');
      attemptsRef.current = 0; // reset backoff on successful connection
      setDisconnected(false);
    };

    websocket.onmessage = (event) => {
      const message = JSON.parse(event.data);

      if (message.type === 'telemetry') {
        setStreamActive(true);
        const point = message.data;
        setTelemetryPoints(prev => {
          const next = mergeTelemetry(prev, [point]);
          const last = next[next.length - 1];
          if (last) setCurrentPosition([last.latitude, last.longitude]);
          return next;
        });
        if (point.speed !== undefined) setCurrentSpeed(point.speed);
        if (point.altitude !== undefined) setCurrentAltitude(point.altitude);
        if (point.heart_rate !== undefined) setCurrentHeartRate(point.heart_rate);
      }

      if (message.type === 'event_info') {
        // If the event already has an end_time when we connect, notify the caller.
        if (message.data?.end_time && onEventEnded) {
          onEventEnded();
        }
        // Server tells us whether the streamer is actively sending data.
        if (typeof message.data?.stream_active === 'boolean') {
          setStreamActive(message.data.stream_active);
        }
      }

      if (message.type === 'event_ended') {
        // The streamer just ended the session in real time.
        if (onEventEnded) {
          onEventEnded();
        }
      }

      if (message.type === 'reaction') {
        const t: ReactionType | undefined = message?.data?.emoji_type;
        if (t === 'heart' || t === 'thumbs_up') addFloating(t);
        if (message?.data?.counts) setReactionCounts(message.data.counts);
      }

      if (message.type === 'narration') {
        const text: string | undefined = message?.data?.text;
        if (text) setNarrationText(text);
      }

      if (message.type === 'spectator_count') {
        const count: number | undefined = message?.data?.count;
        if (typeof count === 'number') setSpectatorCount(count);
      }
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    websocket.onclose = (evt) => {
      wsRef.current = null;

      // Permanent close: at capacity -- do not reconnect.
      if (evt.code === 1008) {
        setCapacityMessage(evt.reason || 'This event is at capacity.');
        return;
      }

      // Permanent close: event no longer live or not found -- do not reconnect.
      if (evt.code === 4410 || evt.code === 4004) {
        return;
      }

      // Normal close triggered by cleanup (unmount / disabled).
      if (unmountedRef.current || !enabled) {
        return;
      }

      // Reconnect with exponential backoff + jitter.
      attemptsRef.current += 1;
      if (attemptsRef.current > MAX_RECONNECT_ATTEMPTS) {
        console.log(`WebSocket gave up after ${MAX_RECONNECT_ATTEMPTS} attempts`);
        setDisconnected(true);
        return;
      }
      const base = Math.min(
        BASE_RECONNECT_DELAY_MS * Math.pow(2, attemptsRef.current - 1),
        MAX_RECONNECT_DELAY_MS,
      );
      // Add up to 25% jitter to prevent thundering herd.
      const jitter = Math.random() * base * 0.25;
      const delay = base + jitter;
      console.log(
        `WebSocket closed (code=${evt.code}). Reconnecting in ${Math.round(delay)}ms (attempt ${attemptsRef.current})`,
      );

      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    };
  }, [hashId, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    unmountedRef.current = false;
    attemptsRef.current = 0;
    connect();

    return () => {
      unmountedRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return { capacityMessage, streamActive, disconnected };
};
